<template>
  <div class="remaining-fees-container">
    <!-- Header Section -->
    <div class="page-header">
      <div class="header-content">
        <div class="header-icon">
          <v-icon size="48" color="primary">mdi-account-cash-outline</v-icon>
        </div>
        <div class="header-text">
          <h1 class="page-title">الطلبة المتأخرين في السداد</h1>
          <p class="page-subtitle">عرض الطلبة الذين لم يسددوا الرسوم كاملة</p>
        </div>
      </div>
    </div>

    <!-- Year Selection -->
    <div class="year-selection">
      <v-card class="selection-card" elevation="4">
        <div class="card-content">
          <div class="input-section">
            <v-select
              v-model="selectedYear"
              :items="studyYears"
              label="اختر السنة الدراسية"
              outlined
              dense
              color="#0E734B"
            ></v-select>
            <v-btn
              color="primary"
              @click="loadStudentsRemainingFees"
              :loading="loading"
              :disabled="!selectedYear"
              class="mt-3"
              block
            >
              <v-icon left>mdi-refresh</v-icon>
              تحميل البيانات
            </v-btn>
          </div>
          <div class="filter-section">
            <v-select
              v-model="selectedSection"
              :items="$store.state.sections"
              label="القسم"
              outlined
              dense
              color="primary"
              clearable
              item-text="sectionName"
              item-value="idSection"
              @change="loadStudentsRemainingFees"
            ></v-select>
            <v-select
              v-model="selectedLevel"
              :items="levels"
              label="المرحلة"
              outlined
              dense
              color="primary"
              clearable
              @change="loadStudentsRemainingFees"
            ></v-select>
          </div>
        </div>
      </v-card>
    </div>

    <!-- Loading Overlay -->
    <v-overlay :value="loading" class="overlay-custom">
      <v-progress-circular indeterminate size="64" color="#ffffff"></v-progress-circular>
    </v-overlay>

    <!-- Statistics Cards -->
    <div class="statistics-section" v-if="students.length > 0">
      <v-row class="stats-row">
        <v-col cols="12" sm="6" md="6">
          <v-card class="stat-card" elevation="1">
            <div class="stat-content">
              <div class="stat-icon">
                <v-icon size="32" color="primary">mdi-account-group</v-icon>
              </div>
              <div class="stat-text">
                <h3 class="stat-title">إجمالي الطلبة</h3>
                <h2 class="stat-value">{{ students.length }}</h2>
              </div>
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="6">
          <v-card class="stat-card" elevation="1">
            <div class="stat-content">
              <div class="stat-icon">
                <v-icon size="32" color="primary">mdi-cash-minus</v-icon>
              </div>
              <div class="stat-text">
                <h3 class="stat-title">إجمالي المتبقي</h3>
                <h2 class="stat-value">{{ totalRemainingAmount.toLocaleString('en-US', { style: 'currency', currency: 'IQD' }) }}</h2>
              </div>
            </div>
          </v-card>
        </v-col> 
      </v-row>
    </div>

    <!-- Students Table -->
    <div class="table-section" v-if="students.length > 0">
      <v-card class="table-card" elevation="4">
        <div class="table-header">
          <h2 class="table-title">قائمة الطلبة المتأخرين في السداد</h2>
          <div class="table-actions">
            <v-btn
              color="#0E734B"
              outlined
              @click="exportToExcel"
              :loading="exporting"
              class="mr-2"
            >
              <v-icon left>mdi-file-excel</v-icon>
              تصدير إلى Excel
            </v-btn>
            <v-btn
              color="#dc3545"
              outlined
              @click="exportToPDF"
              :loading="exporting"
            >
              <v-icon left>mdi-file-pdf</v-icon>
              تصدير إلى PDF
            </v-btn>
          </div>
        </div>
        
        <!-- Search Section -->
        <div class="search-section">
          <v-text-field
            v-model="searchQuery"
            prepend-inner-icon="mdi-magnify"
            label="البحث عن طالب (الاسم أو رقم الكلية)"
            outlined
            dense
            clearable
            color="#0E734B"
            class="search-field"
            hide-details
          ></v-text-field>
        </div>
        
        <v-data-table
          :headers="headers"
          :items="students"
          :items-per-page="20"
          :loading="loading"
          :search="searchQuery"
          :custom-filter="customFilter"
          class="elevation-1"
          :custom-sort="customSort"
          multi-sort
        >
          <template v-slot:item.payment_percentage="{ item }">
            <v-chip
              :color="getPaymentPercentageColor(item.payment_percentage)"
              text-color="white"
              small
            >
              {{ item.payment_percentage }}%
            </v-chip>
          </template>

          <template v-slot:item.remaining_amount="{ item }">
            <span class="remaining-amount">
              {{ item.remaining_amount.toLocaleString('en-US', { style: 'currency', currency: 'IQD' }) }}
            </span>
          </template>

          <template v-slot:item.total_paid="{ item }">
            <span class="paid-amount">
              {{ (item.total_paid || 0).toLocaleString('en-US', { style: 'currency', currency: 'IQD' }) }}
            </span>
          </template>

          <template v-slot:item.installment_amount="{ item }">
            <span class="installment-amount">
              {{ item.installment_amount.toLocaleString('en-US', { style: 'currency', currency: 'IQD' }) }}
            </span>
          </template>

          <template v-slot:item.amount_after_discount="{ item }">
            <span class="amount-after-discount">
              {{ item.amount_after_discount.toLocaleString('en-US', { style: 'currency', currency: 'IQD' }) }}
            </span>
          </template>

          <template v-slot:item.additional_discount_amount="{ item }">
            <span class="additional-discount-amount">
              {{ item.additional_discount_amount.toLocaleString('en-US', { style: 'currency', currency: 'IQD' }) }}
            </span>
          </template>

          <template v-slot:item.amount_after_all_discounts="{ item }">
            <span class="amount-after-all-discounts">
              {{ item.amount_after_all_discounts.toLocaleString('en-US', { style: 'currency', currency: 'IQD' }) }}
            </span>
          </template>

          <template v-slot:item.discount="{ item }">
            <span class="discount-amount">
              {{ (item.discount || 0) }}%
            </span>
          </template>
        </v-data-table>
      </v-card>
    </div>

    <!-- No Data Message -->
    <div class="no-data-section" v-if="!loading && students.length === 0">
      <v-card class="no-data-card" elevation="4">
        <div class="no-data-content">
          <v-icon size="64" color="#ccc">mdi-account-off</v-icon>
          <h3>لا يوجد طلبة متأخرين في السداد</h3>
          <p>جميع الطلبة قد سددوا الرسوم المطلوبة أو لديهم خصم 100%</p>
        </div>
      </v-card>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import jsPDF from 'jspdf';

export default {
  name: 'StudentsRemainingFees',
  data() {
    return {
      loading: false,
      exporting: false,
      selectedYear: null,
      selectedSection: null,
      selectedLevel: null,
      students: [],
      searchQuery: '',
      studyYears: [
        { text: '2024-2025', value: 75 },
        { text: '2023-2024', value: 74 },
        { text: '2022-2023', value: 73 },
        { text: '2025-2026', value: 76 },
      ],
      levels: [
        { text: ' الأول', value: 1 },
        { text: ' الثاني', value: 2 },
        { text: ' الثالث', value: 3 },
        { text: ' الرابع', value: 4 },
        { text: ' الخامس', value: 5 }
      ],
      headers: [
        { text: 'الرقم الجامعي', value: 'collegeNumber', sortable: true },
        { text: 'اسم الطالب', value: 'studentName', sortable: true },
        { text: 'المرحلة', value: 'level', sortable: true },
        { text: 'نوع القبول', value: 'acceptedName', sortable: true },
        { text: 'الخصم', value: 'discount', sortable: true },
        { text: 'المبلغ المطلوب', value: 'installment_amount', sortable: true },
        { text: 'المدفوع', value: 'total_paid', sortable: true },
        { text: 'المتبقي', value: 'remaining_amount', sortable: true },
        { text: 'نسبة السداد', value: 'payment_percentage', sortable: true }
      ]
    };
  },
  computed: {
    totalRemainingAmount() {
      return this.students.reduce((total, student) => total + (student.remaining_amount || 0), 0);
    },
    averagePaymentPercentage() {
      if (this.students.length === 0) return 0;
      const total = this.students.reduce((sum, student) => sum + (student.payment_percentage || 0), 0);
      return total / this.students.length;
    },
    overdueStudentsCount() {
      return this.students.filter(student => (student.payment_percentage || 0) < 50).length;
    }
  },
  created() {
    // Set default year to current year
    this.selectedYear = 75;
    this.loadStudentsRemainingFees();
    console.log(this.$store.state.sections);
  },
  methods: {
    async loadStudentsRemainingFees() {
      if (!this.selectedYear) return;
      
      this.loading = true;
      try {
        const params = {};
        if (this.selectedSection) params.sectionId = this.selectedSection;
        if (this.selectedLevel) params.level = this.selectedLevel;
        
        const response = await axios.get(
          `${this.$store.state.apiUrlFees}/getStudentsRemainingFees/${this.selectedYear}`,
          {
            params,
            headers: { Authorization: `Bearer ${this.$store.state.user}` },
            timeout: 120000
          }
        );

        console.log(response.data);
        
        this.students = response.data;
      } catch (error) {
        console.error('Error loading students remaining fees:', error);
        this.$toasted.error('حدث خطأ في تحميل البيانات');
      } finally {
        this.loading = false;
      }
    },
    
    getPaymentPercentageColor(percentage) {
      if (percentage >= 80) return 'success';
      if (percentage >= 50) return 'warning';
      return 'error';
    },
    
    customSort(items, index, isDesc) {
      items.sort((a, b) => {
        if (index[0] === 'payment_percentage') {
          return isDesc[0] ? b.payment_percentage - a.payment_percentage : a.payment_percentage - b.payment_percentage;
        }
        if (index[0] === 'remaining_amount') {
          return isDesc[0] ? b.remaining_amount - a.remaining_amount : a.remaining_amount - b.remaining_amount;
        }
        return 0;
      });
      return items;
    },
    
    customFilter(value, search, item) {
      if (!search || search.trim() === '') return true;
      
      const searchTerm = search.toString().toLowerCase().trim();
      
      // Search in student name (Arabic)
      const studentName = (item.studentName || '').toString().toLowerCase();
      if (studentName.includes(searchTerm)) return true;
      
      // Search in college number
      const collegeNumber = (item.collegeNumber || '').toString().toLowerCase();
      if (collegeNumber.includes(searchTerm)) return true;
      
      // Search in accepted name (Arabic)
      const acceptedName = (item.acceptedName || '').toString().toLowerCase();
      if (acceptedName.includes(searchTerm)) return true;
      
      // Search in level
      const level = (item.level || '').toString();
      if (level.includes(searchTerm)) return true;
      
      return false;
    },
    
    async exportToExcel() {
      this.exporting = true;
      try {
        // Create Excel data
        const exportData = this.students.map(student => ({
          'الرقم الجامعي': student.collegeNumber,
          'اسم الطالب': student.studentName,
          'المرحلة': student.level,
          'نوع القبول': student.acceptedName,
          'الخصم %': student.discount,
          'المبلغ الأصلي': student.installment_amount,
          'المبلغ بعد الخصم %': student.amount_after_discount,
          'خصم إضافي': student.additional_discount_amount,
          'المبلغ النهائي': student.amount_after_all_discounts,
          'المدفوع': student.total_paid || 0,
          'المتبقي': student.remaining_amount,
          'نسبة السداد': `${student.payment_percentage}%`
        }));
        
        // Import ExcelJS dynamically
        const ExcelJS = await import('exceljs');
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Students Remaining Fees');
        
        // Add headers
        const headers = Object.keys(exportData[0]);
        worksheet.addRow(headers);
        
        // Add data
        exportData.forEach(row => {
          worksheet.addRow(Object.values(row));
        });
        
        // Style headers
        worksheet.getRow(1).font = { bold: true };
        worksheet.getRow(1).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF0E734B' }
        };
        worksheet.getRow(1).font = { color: { argb: 'FFFFFFFF' }, bold: true };
        
        // Auto-fit columns
        worksheet.columns.forEach(column => {
          column.width = 15;
        });
        
        // Generate file
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `students_remaining_fees_${this.selectedYear}.xlsx`;
        link.click();
        window.URL.revokeObjectURL(url);
        
        this.$toasted.success('تم تصدير البيانات بنجاح');
      } catch (error) {
        console.error('Error exporting to Excel:', error);
        this.$toasted.error('حدث خطأ في تصدير البيانات');
      } finally {
        this.exporting = false;
      }
    },
    
    async exportToPDF() {
      this.exporting = true;
      try {
        // Create PDF document
        const doc = new jsPDF('l', 'mm', 'a4'); // Landscape orientation
        
        // Add Arabic font support
        const arabicFont = new URL(
          `./../assets/IBMPlexSansArabic-Medium.ttf`,
          import.meta.url
        ).href;
        doc.addFileToVFS("IBMPlexSansArabic-Medium.ttf", arabicFont);
        doc.addFont(arabicFont, "IBMPlexSansArabic-Medium", "normal");
        doc.setFont("IBMPlexSansArabic-Medium");
        
        // Add title in Arabic
        doc.setFontSize(18);
        doc.setTextColor(14, 115, 75); // #0E734B color
        doc.text('تقرير الطلبة المتأخرين في السداد', 20, 20);
        
        // Add year info
        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0);
        doc.text(`السنة الدراسية: ${this.selectedYear}`, 20, 30);
        
        // Add date
        const currentDate = new Date().toLocaleDateString('ar-IQ');
        doc.text(`تاريخ التقرير`, 20, 35);
        doc.text(` ${currentDate}`, 20, 40);
        
        // Add statistics
        doc.setFontSize(10);
        doc.text(`إجمالي الطلبة: ${this.students.length}`, 20, 45);
        doc.text(`إجمالي المتبقي: ${this.totalRemainingAmount.toLocaleString('en-US', { style: 'currency', currency: 'IQD' })}`, 20, 50);
        
        // Table headers in Arabic
        const headers = [
          'الرقم الجامعي',
          'اسم الطالب', 
          'المرحلة',
          'نوع القبول',
          'الخصم %',
          'المبلغ الأصلي',
          'بعد الخصم %',
          'خصم إضافي',
          'المبلغ النهائي',
          'المدفوع',
          'المتبقي',
          'نسبة السداد %'
        ];
        
        // Table data with proper formatting
        const tableData = this.students.map(student => [
          student.collegeNumber || '',
          student.studentName || '',
          student.level || '',
          student.acceptedName || '',
          `${student.discount || 0}%`,
          student.installment_amount ? `${student.installment_amount.toLocaleString()} IQD` : '0 IQD',
          student.amount_after_discount ? `${student.amount_after_discount.toLocaleString()} IQD` : '0 IQD',
          student.additional_discount_amount ? `${student.additional_discount_amount.toLocaleString()} IQD` : '0 IQD',
          student.amount_after_all_discounts ? `${student.amount_after_all_discounts.toLocaleString()} IQD` : '0 IQD',
          student.total_paid ? `${student.total_paid.toLocaleString()} IQD` : '0 IQD',
          student.remaining_amount ? `${student.remaining_amount.toLocaleString()} IQD` : '0 IQD',
          `${student.payment_percentage || 0}%`
        ]);
        
        // Use manual table drawing for better reliability
        let yPosition = 60;
        const pageHeight = doc.internal.pageSize.height;
        const pageWidth = doc.internal.pageSize.width;
        const rowHeight = 8;
        const tableWidth = pageWidth - 40; // Leave 20mm margin on each side
        
        // Calculate column widths to fit within page - better distribution
        const totalWidth = tableWidth - 10;
        const columnWidths = [
          totalWidth * 0.10, // الرقم الجامعي - 10%
          totalWidth * 0.18, // اسم الطالب - 18%
          totalWidth * 0.06, // المرحلة - 6%
          totalWidth * 0.20, // نوع القبول - 20% (increased for longer text)
          totalWidth * 0.06, // الخصم % - 6%
          totalWidth * 0.12, // المبلغ الأصلي - 12%
          totalWidth * 0.12, // بعد الخصم % - 12%
          totalWidth * 0.10, // خصم إضافي - 10%
          totalWidth * 0.12, // المبلغ النهائي - 12%
          totalWidth * 0.10, // المدفوع - 10%
          totalWidth * 0.12, // المتبقي - 12%
          totalWidth * 0.08  // نسبة السداد % - 8%
        ];
        
        // Draw headers
        doc.setFillColor(14, 115, 75);
        doc.rect(20, yPosition, tableWidth, rowHeight, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(8);
        
        let xPosition = 25;
        headers.forEach((header, index) => {
          // Center text in column
          const textWidth = doc.getTextWidth(header);
          const centerX = xPosition + (columnWidths[index] - textWidth) / 2;
          // Ensure text doesn't go outside column bounds
          const finalX = Math.max(xPosition + 1, Math.min(centerX, xPosition + columnWidths[index] - textWidth - 1));
          doc.text(header, finalX, yPosition + 5);
          xPosition += columnWidths[index];
        });
        
        yPosition += rowHeight;
        
        // Draw data rows
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(7);
        tableData.forEach((row, rowIndex) => {
          if (yPosition > pageHeight - 20) {
            doc.addPage();
            yPosition = 20;
          }
          
          // Alternate row colors
          if (rowIndex % 2 === 0) {
            doc.setFillColor(248, 249, 250);
            doc.rect(20, yPosition, tableWidth, rowHeight, 'F');
          }
          
          xPosition = 25;
          row.forEach((cell, cellIndex) => {
            // Truncate long text to fit in column
            let cellText = cell.toString();
            let maxLength;
            
            // Different max lengths for different column types
            if (cellIndex === 0) {
              // College number - allow more characters
              maxLength = Math.floor(columnWidths[cellIndex] / 1.5);
            } else if (cellIndex === 1 || cellIndex === 3) {
              // Student name and acceptance type - allow more characters
              maxLength = Math.floor(columnWidths[cellIndex] / 1.2);
            } else if (cellIndex === 2 || cellIndex === 4 || cellIndex === 11) {
              // Level, discount %, payment % - shorter
              maxLength = Math.floor(columnWidths[cellIndex] / 2);
            } else {
              // Amount columns - allow more for numbers
              maxLength = Math.floor(columnWidths[cellIndex] / 1.3);
            }
            
            if (cellText.length > maxLength) {
              cellText = cellText.substring(0, maxLength - 3) + '...';
            }
            
            // Align text based on column type
            let alignX = xPosition;
            const textWidth = doc.getTextWidth(cellText);
            
            if (cellIndex === 0 || cellIndex === 2 || cellIndex === 4 || cellIndex === 11) {
              // Center align for numbers and percentages
              alignX = xPosition + (columnWidths[cellIndex] - textWidth) / 2;
            } else if (cellIndex === 1 || cellIndex === 3) {
              // Right align for Arabic text (names and acceptance types)
              alignX = xPosition + columnWidths[cellIndex] - textWidth - 1;
            } else {
              // Right align for amounts (all monetary values)
              alignX = xPosition + columnWidths[cellIndex] - textWidth - 1;
            }
            
            doc.text(cellText, alignX, yPosition + 5);
            xPosition += columnWidths[cellIndex];
          });
          
          yPosition += rowHeight;
        });
        
        // Add footer
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
          doc.setPage(i);
          doc.setFontSize(8);
          doc.setTextColor(128, 128, 128);
          doc.text(`صفحة ${i} من ${pageCount}`, 20, doc.internal.pageSize.height - 10);
          // doc.text(`تاريخ الطباعة: ${currentDate}`, doc.internal.pageSize.width - 60, doc.internal.pageSize.height - 10);
        }
        
        // Print the PDF directly instead of downloading
        const pdfBlob = doc.output('blob');
        const pdfUrl = URL.createObjectURL(pdfBlob);
        
        // Open in new window for printing
        const printWindow = window.open(pdfUrl, '_blank');
        if (printWindow) {
          printWindow.onload = function() {
            printWindow.print();
            // Clean up the URL after printing
            setTimeout(() => {
              URL.revokeObjectURL(pdfUrl);
            }, 1000);
          };
        }
        
        this.$toasted.success('تم فتح نافذة الطباعة بنجاح');
      } catch (error) {
        console.error('Error exporting to PDF:', error);
        this.$toasted.error('حدث خطأ في تصدير البيانات إلى PDF');
      } finally {
        this.exporting = false;
      }
    }
  }
};
</script>

<style scoped>
.remaining-fees-container {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  min-height: 100vh;
  padding: 25px;
}

.page-header {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 24px;
  padding: 35px;
  margin-bottom: 30px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(233, 236, 239, 0.8);
  position: relative;
  overflow: hidden;
}

.page-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background:linear-gradient(90deg, #262827 0%, #757777 50%, #797979 100%)
}

.header-content {
  display: flex;
  align-items: center;
  gap: 25px;
}

.header-icon {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 20px;
  padding: 20px;
  border: 2px solid rgba(233, 236, 239, 0.8);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
}

.header-text {
  flex: 1;
}

.page-title {
  font-size: 2.4rem;
  font-weight: 700;
  margin: 0 0 10px 0;
  text-align: right;
  text-shadow: 0 1px 2px rgba(14, 115, 75, 0.1);
}

.page-subtitle {
  color: #6c757d;
  font-size: 1.2rem;
  margin: 0;
  text-align: right;
  opacity: 0.8;
}

.year-selection {
  margin-bottom: 30px;
}

.selection-card {
  border-radius: 20px;
  border: 1px solid rgba(233, 236, 239, 0.8);
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
}

.card-content {
  padding: 25px;
}

.input-section {
  margin-bottom: 20px;
}

.filter-section {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.statistics-section {
  margin-bottom: 30px;
}

.stats-row {
  margin: 0;
}

.stat-card {
  border-radius: 10px;
  border: 1px solid rgba(233, 236, 239, 0.8);
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  transition: all 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(14, 115, 75, 0.15);
}

.stat-content {
  padding: 25px;
  display: flex;
  align-items: center;
  gap: 20px;
}

.stat-icon {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);

  border-radius: 15px;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.stat-text {
  flex: 1;
}

.stat-title {
  color: #6c757d;
  font-size: 0.9rem;
  margin: 0 0 8px 0;
  text-align: right;
}

.stat-value {
  color: #0E734B;
  font-size: 1.8rem;
  font-weight: 700;
  margin: 0;
  text-align: right;
}

.table-section {
  margin-bottom: 30px;
}

.table-card {
  border-radius: 20px;
  border: 1px solid rgba(233, 236, 239, 0.8);
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  overflow: hidden;
}

.table-header {
  padding: 25px;
  border-bottom: 1px solid rgba(233, 236, 239, 0.8);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.table-title {
  color: #1c1d1c;
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
}

.table-actions {
  display: flex;
  gap: 15px;
}

.table-actions .v-btn {
  min-width: 140px;
}

.search-section {
  padding: 20px 25px;
  border-bottom: 1px solid rgba(233, 236, 239, 0.8);
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
}

.search-field {
  max-width: 400px;
  margin: 0 auto;
}

.search-field .v-input__control .v-input__slot {
  border-radius: 12px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.search-field .v-text-field__details {
  display: none;
}

.remaining-amount {
  color: #dc3545;
  font-weight: 600;
}

.paid-amount {
  color: #28a745;
  font-weight: 600;
}

.installment-amount {
  color: #0E734B;
  font-weight: 600;
}

.amount-after-discount {
  color: #007bff; /* A different color for the new field */
  font-weight: 600;
}

.additional-discount-amount {
  color: #6c757d; /* A different color for the new field */
  font-weight: 600;
}

.amount-after-all-discounts {
  color: #28a745; /* A different color for the new field */
  font-weight: 600;
}

.discount-amount {
  color: #a1801a;
  font-weight: 600;
}

.no-data-section {
  margin-top: 50px;
}

.no-data-card {
  border-radius: 20px;
  border: 1px solid rgba(233, 236, 239, 0.8);
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
}

.no-data-content {
  padding: 50px;
  text-align: center;
  color: #6c757d;
}

.no-data-content h3 {
  margin: 20px 0 10px 0;
  color: #495057;
}

.no-data-content p {
  margin: 0;
  opacity: 0.8;
}



/* Responsive Design */
@media (max-width: 960px) {
  .page-title {
    font-size: 2rem;
  }
  
  .filter-section {
    flex-direction: column;
  }
  
  .table-header {
    flex-direction: column;
    gap: 15px;
    align-items: flex-start;
  }
}

@media (max-width: 600px) {
  .remaining-fees-container {
    padding: 20px;
  }
  
  .page-header {
    padding: 25px;
    border-radius: 20px;
  }
  
  .page-title {
    font-size: 1.8rem;
    text-align: center;
  }
  
  .page-subtitle {
    text-align: center;
  }
  
  .header-content {
    flex-direction: column;
    text-align: center;
    gap: 20px;
  }
  
  .stat-content {
    flex-direction: column;
    text-align: center;
    gap: 15px;
  }
  
  .stat-title, .stat-value {
    text-align: center;
  }
}
</style>
